// The SHOPPER CROWD.
//
// Everyone in the shop who is not the player. The whole crowd is three
// InstancedMeshes and no lights: a capsule body, a sphere head, and one sleeve
// mesh allocated at 2n instances — instance i is the record person i carries
// under their arm, instance n+i is the record they are holding up while they
// dig. Both sleeve instances sample the shared cover atlas, so each one shows a
// different cover in a single draw call: `aTile` is an instanced vec2 of atlas
// cell coordinates, and a small onBeforeCompile patch folds it into `vMapUv`
// after the standard <uv_vertex> chunk has run.
//
// Movement is a three-state machine per person — walk / browse / pay. Routing
// is coarse: `aisleRoute` snaps both ends of a trip to the nearest AISLE_X line
// and walks the dog-leg, and `pushOutOfColliders` shoves anyone who ends up
// inside a fixture out through its nearest inflated face. A person who stops
// making progress for three seconds skips a leg or gives up and browses.
//
// Buyers accumulate records up to CARRY_MAX and then walk to the counter, wait
// PAY_SECONDS, and leave empty-handed.
//
// Recovered from the production bundle. Numbers and strings are byte-identical
// to the shipped source.

import * as THREE from 'three';
import { AISLE_X, ROOM_HALF_W } from './zones.js';
import { ROOM } from './room.js';
import { COLLIDERS } from './store.js';
import { figureGeometry, figureMaterial, FIGURE_HEIGHT } from './figureData.js';
import { coverAtlas, genreCell, ATLAS_COLS, ATLAS_ROWS } from './atlases.js';

const SHOPPER_CLEARANCE = .22;

// Push a point out of every fixture box it has strayed inside, along whichever
// of the four inflated faces is nearest.
function pushOutOfColliders(point) {
    for (const box of COLLIDERS) {
        if (point.x > box.minX - SHOPPER_CLEARANCE && point.x < box.maxX + SHOPPER_CLEARANCE
            && point.z > box.minZ - SHOPPER_CLEARANCE && point.z < box.maxZ + SHOPPER_CLEARANCE) {
            const toMinX = Math.abs(point.x - (box.minX - SHOPPER_CLEARANCE));
            const toMaxX = Math.abs(point.x - (box.maxX + SHOPPER_CLEARANCE));
            const toMinZ = Math.abs(point.z - (box.minZ - SHOPPER_CLEARANCE));
            const toMaxZ = Math.abs(point.z - (box.maxZ + SHOPPER_CLEARANCE));
            const nearest = Math.min(toMinX, toMaxX, toMinZ, toMaxZ);
            if (nearest === toMinX) point.x = box.minX - SHOPPER_CLEARANCE;
            else if (nearest === toMaxX) point.x = box.maxX + SHOPPER_CLEARANCE;
            else if (nearest === toMinZ) point.z = box.minZ - SHOPPER_CLEARANCE;
            else point.z = box.maxZ + SHOPPER_CLEARANCE;
        }
    }
    return point;
}

// PATHFINDING FOR THE TRIP TO THE TILL.
//
// aisleRoute assumes a shopper can step sideways onto an aisle line wherever they
// stand and cross between lines at the destination's z. That holds for racks in
// the aisles; it does not hold for the till, which sits on the centre line behind
// the kiosk with a fixture beside its apron. Routed that way, most shoppers shoved
// against a bin or the kiosk and the stall watchdog abandoned the trip, so nearly
// nobody paid.
//
// This is a grid A* over the same COLLIDERS list the push-out uses, so a path it
// finds is one pushOutOfColliders will not fight. Cells are blocked a little wider
// than SHOPPER_CLEARANCE so the path keeps off the edge of the push-out zone.
const PATH_CELL = .15;
// Tried in order. The first keeps off the edge of the push-out zone; the last is
// the push-out's own clearance, which is as tight as the real physics allows. A
// browse spot can sit in a gap too narrow for the wider margin (one does, beside
// the old counter's stool), and a shopper there still has to be able to leave.
const PATH_MARGINS = [SHOPPER_CLEARANCE + .04, SHOPPER_CLEARANCE];
const pathGrids = new Map();

function buildPathGrid(margin) {
    const x0 = -(ROOM_HALF_W - .3), x1 = ROOM_HALF_W - .3;
    const z0 = -(ROOM.d / 2 - .3), z1 = ROOM.d / 2 - .3;
    const cols = Math.floor((x1 - x0) / PATH_CELL) + 1;
    const rows = Math.floor((z1 - z0) / PATH_CELL) + 1;
    const blocked = new Uint8Array(cols * rows);
    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
            const x = x0 + c * PATH_CELL, z = z0 + r * PATH_CELL;
            for (const box of COLLIDERS) {
                if (x > box.minX - margin && x < box.maxX + margin
                    && z > box.minZ - margin && z < box.maxZ + margin) {
                    blocked[r * cols + c] = 1;
                    break;
                }
            }
        }
    }
    return { x0, z0, cols, rows, blocked };
}

function cellOf(grid, point) {
    const c = Math.max(0, Math.min(grid.cols - 1, Math.round((point.x - grid.x0) / PATH_CELL)));
    const r = Math.max(0, Math.min(grid.rows - 1, Math.round((point.z - grid.z0) / PATH_CELL)));
    return [c, r];
}

// The nearest open cell to a point: a shopper standing in the fringe of a push-out
// zone, or a stop that falls in a blocked cell, still needs somewhere to start.
function openCellNear(grid, point) {
    const [c0, r0] = cellOf(grid, point);
    if (!grid.blocked[r0 * grid.cols + c0]) return [c0, r0];
    for (let ring = 1; ring < 20; ring++) {
        let best = null, bestD = Infinity;
        for (let dr = -ring; dr <= ring; dr++) {
            for (let dc = -ring; dc <= ring; dc++) {
                if (Math.max(Math.abs(dr), Math.abs(dc)) !== ring) continue;
                const c = c0 + dc, r = r0 + dr;
                if (c < 0 || r < 0 || c >= grid.cols || r >= grid.rows || grid.blocked[r * grid.cols + c]) continue;
                const d = dc * dc + dr * dr;
                if (d < bestD) { bestD = d; best = [c, r]; }
            }
        }
        if (best) return best;
    }
    return null;
}

function clearLine(grid, a, b) {
    const steps = Math.ceil(Math.hypot(b.x - a.x, b.z - a.z) / (PATH_CELL / 2));
    for (let i = 0; i <= steps; i++) {
        const t = steps ? i / steps : 0;
        const [c, r] = cellOf(grid, { x: a.x + (b.x - a.x) * t, z: a.z + (b.z - a.z) * t });
        if (grid.blocked[r * grid.cols + c]) return false;
    }
    return true;
}

// Which stops can a shopper actually get out of? A flood fill from the till over
// the tightest grid. A stop sealed into a pocket - the north end of the centre
// aisle is one, closed off by a stool-sized collider across a corridor narrower
// than a shopper's clearance - would trap whoever chose it: they could browse
// there but never leave to pay. Stops outside the main-room grid (the alcoves)
// are left alone; they were never routed by this.
function reachableStops(stops, counter) {
    const margin = PATH_MARGINS[PATH_MARGINS.length - 1];
    if (!pathGrids.has(margin)) pathGrids.set(margin, buildPathGrid(margin));
    const grid = pathGrids.get(margin);
    const goal = openCellNear(grid, counter);
    if (!goal) return stops;
    const { cols, rows } = grid;
    const seen = new Uint8Array(cols * rows);
    const stack = [goal];
    seen[goal[1] * cols + goal[0]] = 1;
    while (stack.length) {
        const [c, r] = stack.pop();
        for (let dr = -1; dr <= 1; dr++) {
            for (let dc = -1; dc <= 1; dc++) {
                const nc = c + dc, nr = r + dr, n = nr * cols + nc;
                if ((!dc && !dr) || nc < 0 || nr < 0 || nc >= cols || nr >= rows || seen[n] || grid.blocked[n]) continue;
                seen[n] = 1;
                stack.push([nc, nr]);
            }
        }
    }
    const inGrid = (stop) => Math.abs(stop.x) <= ROOM_HALF_W - .3 && Math.abs(stop.z) <= ROOM.d / 2 - .3;
    return stops.filter((stop) => {
        if (!inGrid(stop)) return true;
        const cell = openCellNear(grid, stop);
        return cell && seen[cell[1] * cols + cell[0]];
    });
}

// Waypoints from `from` to `to` around every collider, or null if there is no way.
function findPath(from, to) {
    for (const margin of PATH_MARGINS) {
        if (!pathGrids.has(margin)) pathGrids.set(margin, buildPathGrid(margin));
        const path = searchGrid(pathGrids.get(margin), from, to);
        if (path) return path;
    }
    return null;
}

function searchGrid(grid, from, to) {
    const start = openCellNear(grid, from), goal = openCellNear(grid, to);
    if (!start || !goal) return null;
    const { cols, rows } = grid;
    const index = (c, r) => r * cols + c;
    const g = new Float32Array(cols * rows).fill(Infinity);
    const parent = new Int32Array(cols * rows).fill(-1);
    const closed = new Uint8Array(cols * rows);
    const open = [[0, index(start[0], start[1])]];
    g[index(start[0], start[1])] = 0;
    const heuristic = (c, r) => {
        const dx = Math.abs(c - goal[0]), dz = Math.abs(r - goal[1]);
        return (dx + dz) + (Math.SQRT2 - 2) * Math.min(dx, dz);
    };
    let found = false;
    while (open.length) {
        let best = 0;
        for (let i = 1; i < open.length; i++) if (open[i][0] < open[best][0]) best = i;
        const [, cur] = open.splice(best, 1)[0];
        if (closed[cur]) continue;
        closed[cur] = 1;
        const c = cur % cols, r = (cur - c) / cols;
        if (c === goal[0] && r === goal[1]) { found = true; break; }
        for (let dr = -1; dr <= 1; dr++) {
            for (let dc = -1; dc <= 1; dc++) {
                if (!dc && !dr) continue;
                const nc = c + dc, nr = r + dr;
                if (nc < 0 || nr < 0 || nc >= cols || nr >= rows) continue;
                const n = index(nc, nr);
                if (grid.blocked[n] || closed[n]) continue;
                // no cutting a corner between two blocked cells
                if (dc && dr && (grid.blocked[index(c + dc, r)] || grid.blocked[index(c, r + dr)])) continue;
                const cost = g[cur] + (dc && dr ? Math.SQRT2 : 1);
                if (cost < g[n]) {
                    g[n] = cost;
                    parent[n] = cur;
                    open.push([cost + heuristic(nc, nr), n]);
                }
            }
        }
    }
    if (!found) return null;

    const cells = [];
    for (let n = index(goal[0], goal[1]); n !== -1; n = parent[n]) {
        const c = n % cols, r = (n - c) / cols;
        cells.push({ x: grid.x0 + c * PATH_CELL, z: grid.z0 + r * PATH_CELL });
    }
    cells.reverse();

    // Pull the path taut: from each waypoint, jump to the furthest one in sight.
    const out = [];
    let at = 0;
    while (at < cells.length - 1) {
        let next = cells.length - 1;
        while (next > at + 1 && !clearLine(grid, cells[at], cells[next])) next--;
        out.push(cells[next]);
        at = next;
    }
    out.push({ x: to.x, z: to.z });
    return out;
}

// Waypoints from `from` to `to` that keep to the aisles: step sideways onto the
// nearest aisle line, run along it, cross over to the destination's aisle line
// if it is a different one, then step off at the target.
function aisleRoute(from, to) {
    const fromAisle = AISLE_X.reduce((best, x) => Math.abs(x - from.x) < Math.abs(best - from.x) ? x : best, AISLE_X[0]);
    const toAisle = AISLE_X.reduce((best, x) => Math.abs(x - to.x) < Math.abs(best - to.x) ? x : best, AISLE_X[0]);
    const legs = [];
    if (Math.abs(from.x - fromAisle) > .05) legs.push({
        x: fromAisle,
        z: from.z
    });
    if (Math.abs(fromAisle - toAisle) > .05) {
        legs.push({
            x: fromAisle,
            z: to.z
        }, {
            x: toAisle,
            z: to.z
        });
    } else {
        legs.push({
            x: fromAisle,
            z: to.z
        });
    }
    legs.push({
        x: to.x,
        z: to.z
    });
    return legs;
}

const HEAD_RADIUS = .115;
const SHOPPER_HEIGHT = 1.14;
const JACKET_TONES = [0xb5483a, 0x3f6b8f, 0xd9b44a, 0x4f7a55, 0x8a5a8c, 0xc9c2b2, 0xd77a3a, 0x2f3e5c];
const FIGURE_SCALE = (SHOPPER_HEIGHT + .14 + HEAD_RADIUS) / FIGURE_HEIGHT;
const WALK_SPEED = .55;
const DWELL_MIN = 4;
const DWELL_MAX = 11;
const DIG_BEND = .3;
const PICK_CHANCE = .3;
const CARRY_MAX = 2;
const SHOPPER_GENRES = ["soul", "jazz", "funk", "rock", "hiphop", "reggae", "electronic", "classical", "latin", "disco"];
const PAY_SECONDS = 5;
const SHOPPER_SLEEVE_SIZE = .31;

class Shoppers {
    constructor(scene, stops = [], count = 5, counter = null) {
        this.t = 0;
        this.stops = stops.filter(Boolean);
        this.counter = counter;
        // Only browse where it is possible to walk away from again.
        if (counter) {
            const before = this.stops.length;
            this.stops = reachableStops(this.stops, counter);
            this.droppedUnreachableStops = before - this.stops.length;
        }
        this.n = Math.min(count, Math.max(1, this.stops.length));

        // One modelled figure (src/figureData.js), pivot at the feet, scaled to
        // the old capsule-and-head height so the dig bend and the colliders are
        // unchanged. Each person gets a jacket tone through the instance colour.
        this.body = new THREE.InstancedMesh(figureGeometry(), figureMaterial(false), this.n);
        const jacket = new THREE.Color();
        for (let i = 0; i < this.n; i++) {
            this.body.setColorAt(i, jacket.setHex(JACKET_TONES[i % JACKET_TONES.length]));
        }
        this.body.instanceColor.needsUpdate = true;

        const atlas = coverAtlas();
        const sleeveMaterial = new THREE.MeshStandardMaterial({
            map: atlas.tex,
            roughness: .62,
            metalness: 0
        });
        // Per-instance atlas cell: offset the mapped UV by the instance's tile
        // and divide down into one cell of the cover atlas.
        sleeveMaterial.onBeforeCompile = (shader) => {
            shader.vertexShader = shader.vertexShader.replace("#include <common>", `#include <common>
attribute vec2 aTile;`).replace("#include <uv_vertex>", `#include <uv_vertex>
	vMapUv = (vMapUv + aTile) / vec2(` + ATLAS_COLS.toFixed(1) + ", " + ATLAS_ROWS.toFixed(1) + ");");
        };
        this.sleeve = new THREE.InstancedMesh(new THREE.BoxGeometry(SHOPPER_SLEEVE_SIZE, SHOPPER_SLEEVE_SIZE, .014), sleeveMaterial, this.n * 2);
        this._tile = new THREE.InstancedBufferAttribute(new Float32Array(this.n * 2 * 2), 2);
        this.sleeve.geometry.setAttribute("aTile", this._tile);
        this._setTile = (instance, tile) => {
            const col = tile % ATLAS_COLS;
            const row = ATLAS_ROWS - 1 - Math.floor(tile / ATLAS_COLS) % ATLAS_ROWS;
            this._tile.setXY(instance, col, row);
            this._tile.needsUpdate = true;
        };
        this.sleeve.frustumCulled = false;
        this.sleeve.castShadow = true;
        this.body.castShadow = true;
        this.body.frustumCulled = false;
        scene.add(this.body, this.sleeve);

        // Scale used to hide an unused instance — 1e-4 on every axis, not 0.
        this._zero = new THREE.Vector3(1e-4, 1e-4, 1e-4);
        this._m = new THREE.Matrix4();
        this._q = new THREE.Quaternion();
        this._v = new THREE.Vector3();
        this._s = new THREE.Vector3(1, 1, 1);
        this._e = new THREE.Euler();
        this._qp = new THREE.Quaternion();
        this._ax = new THREE.Vector3();

        this.people = [];
        for (let i = 0; i < this.n; i++) {
            const start = this.stops[i % this.stops.length];
            const next = this.stops[(i + 1 + i % 3) % this.stops.length];
            this.people.push({
                pos: new THREE.Vector3(start.x, 0, start.z),
                from: new THREE.Vector3(start.x, 0, start.z),
                to: new THREE.Vector3(next.x, 0, next.z),
                dest: {
                    x: next.x,
                    z: next.z
                },
                legs: aisleRoute(start, next),
                leg: 0,
                yaw: start.yaw ?? 0,
                phase: i * 2.7 % 6,
                dwell: DWELL_MIN + i * 3.1 % (DWELL_MAX - DWELL_MIN),
                state: i % 2 ? "browse" : "walk",
                clock: i * 1.9 % 5,
                carry: 0,
                tile: 0,
                flipT: 0,
                buyer: i % 5 < 3,
                paying: false,
                rng: (i * .3719 + .117) % 1
            });
        }
    }

    // One of the eight nearest stops more than .4 away, chosen by an index
    // made from the person's slot and the whole seconds elapsed.
    _nearby(person, index) {
        const ranked = this.stops.map((stop, i) => [Math.hypot(stop.x - person.pos.x, stop.z - person.pos.z), i])
            .filter(([distance]) => distance > .4)
            .sort((a, b) => a[0] - b[0])
            .slice(0, 8);
        return ranked.length ? this.stops[ranked[(index * 3 + Math.floor(this.t)) % ranked.length][1]] : this.stops[0];
    }

    _sendTo(person, index, stop) {
        if (stop) {
            person.state = "walk";
            person.clock = 0;
            person.to.set(stop.x, 0, stop.z);
            person.dest = {
                x: stop.x,
                z: stop.z
            };
            if (stop === this.counter) {
                // The till gets a real path around the bins and the kiosk. The lane
                // route below is the fallback if no path exists.
                const laneX = person.pos.x < 0 ? -1.65 : 1.65;
                person.legs = findPath(person.pos, stop) || [{
                    x: laneX,
                    z: person.pos.z
                }, {
                    x: laneX,
                    z: stop.z
                }, {
                    x: stop.x,
                    z: stop.z
                }];
            } else {
                person.legs = aisleRoute({
                    x: person.pos.x,
                    z: person.pos.z
                }, stop);
            }
            person.leg = 0;
            person.stuck = 0;
            person.last = person.pos.clone();
        }
    }

    update(dt) {
        this.t += dt;
        for (let i = 0; i < this.people.length; i++) {
            const person = this.people[i];
            person.clock += dt;
            let bob = 0;

            if (person.state === "walk") {
                const waypoint = person.legs && person.legs[person.leg];
                if (waypoint) this._v.set(waypoint.x, 0, waypoint.z).sub(person.pos);
                else this._v.copy(person.to).sub(person.pos);
                const distance = this._v.length();

                if (distance < .1 && person.legs && person.leg < person.legs.length - 1) {
                    person.leg++;
                } else if (distance < .1) {
                    // Arrived. Take the stop's facing if it has one.
                    person.state = person.paying ? "pay" : "browse";
                    person.clock = 0;
                    const arrivedAt = person.paying
                        ? this.counter
                        : this.stops.find((stop) => Math.abs(stop.x - person.dest.x) < .01 && Math.abs(stop.z - person.dest.z) < .01);
                    if (arrivedAt && arrivedAt.yaw != null) person.yaw = arrivedAt.yaw;
                } else {
                    this._v.multiplyScalar(Math.min(1, WALK_SPEED * dt / distance));
                    person.pos.add(this._v);
                    pushOutOfColliders(person.pos);
                    if (!person.last) person.last = person.pos.clone();
                    // Stuck means moved under a quarter of the step just attempted. This
                    // compared against a flat 0.02 m per frame, but WALK_SPEED is 0.55 m/s
                    // - about 0.009 m per frame - so every walking shopper counted as stuck
                    // and any leg over three seconds was abandoned, which is why almost
                    // nobody finished a trip to the till.
                    person.stuck = person.pos.distanceTo(person.last) < WALK_SPEED * dt * .25 ? (person.stuck || 0) + dt : 0;
                    person.last.copy(person.pos);
                    if (person.stuck > 3) {
                        person.stuck = 0;
                        if (person.legs && person.leg < person.legs.length - 1) {
                            person.leg++;
                        } else {
                            person.state = "browse";
                            person.clock = 0;
                            person.paying = false;
                        }
                    }
                    let turn = Math.atan2(this._v.x, this._v.z) - person.yaw;
                    while (turn > Math.PI) turn -= Math.PI * 2;
                    while (turn < -Math.PI) turn += Math.PI * 2;
                    person.yaw += turn * Math.min(1, dt * 4);
                    bob = Math.abs(Math.sin((this.t + person.phase) * 4.4)) * .03;
                }
            } else if (person.state === "pay") {
                if (person.clock > PAY_SECONDS) {
                    person.carry = 0;
                    person.paying = false;
                    this._sendTo(person, i, this.stops[(i * 7 + Math.floor(this.t)) % this.stops.length]);
                }
            } else if (person.clock > person.dwell) {
                person.rng = Math.abs(Math.sin((this.t + i * 78.233) * 12.9898) * 43758.5453) % 1;
                if (person.buyer && person.rng < PICK_CHANCE && person.carry < CARRY_MAX) {
                    person.carry++;
                    person.tile = genreCell(SHOPPER_GENRES[Math.floor(person.rng * SHOPPER_GENRES.length)], Math.floor(person.rng * 991));
                    this._setTile(i, person.tile);
                }
                person.from.copy(person.pos);
                if (person.carry >= CARRY_MAX && this.counter) {
                    person.paying = true;
                    this._sendTo(person, i, this.counter);
                } else {
                    this._sendTo(person, i, this._nearby(person, i));
                }
            }

            // How far forward this person is bent over the crate, eased in and
            // out over the first and last .6s of the dwell.
            let bend = 0;
            if (person.state === "browse") {
                bob = Math.sin((this.t + person.phase) * 1.1) * .008;
                const ease = Math.min(1, person.clock / .6) * Math.min(1, (person.dwell - person.clock) / .6);
                bend = DIG_BEND * Math.max(0, ease) * (.72 + .28 * Math.sin((this.t + person.phase) * 2.6));
                bob -= bend * .16;
            } else if (person.state === "pay") {
                bob = Math.sin((this.t + person.phase) * 1.1) * .008;
            }

            this._q.setFromEuler(this._e.set(0, person.yaw, 0, "YXZ"));
            this._qp.setFromAxisAngle(this._ax.set(Math.cos(person.yaw), 0, -Math.sin(person.yaw)), bend);
            this._q.premultiply(this._qp);

            const cosYaw = Math.cos(person.yaw);
            const sinYaw = Math.sin(person.yaw);

            // The figure's origin is at its feet, so the dig bend swings the whole
            // body about the floor point.
            this._v.set(person.pos.x, bob, person.pos.z);
            this._s.set(FIGURE_SCALE, FIGURE_SCALE, FIGURE_SCALE);
            this._m.compose(this._v, this._q, this._s);
            this._s.set(1, 1, 1);
            this.body.setMatrixAt(i, this._m);

            // Sleeve instance n+i: the record held up and turned over while
            // digging. Parked below the floor at zero scale when not digging.
            const flipInstance = this.people.length + i;
            if (person.state === "browse" && bend > .04) {
                if (this.t - person.flipT > .85) {
                    person.flipT = this.t;
                    person.rng = Math.abs(Math.sin((this.t + i * 12.9) * 78.233) * 43758.5453) % 1;
                    this._setTile(flipInstance, genreCell(SHOPPER_GENRES[Math.floor(person.rng * SHOPPER_GENRES.length)], Math.floor(person.rng * 997)));
                }
                const flipCos = Math.cos(person.yaw);
                const flipSin = Math.sin(person.yaw);
                const sway = Math.sin((this.t + person.phase) * 2.6);
                this._v.set(person.pos.x + flipSin * .4 - flipCos * sway * .05, 1.02 - bend * .3, person.pos.z + flipCos * .4 + flipSin * sway * .05);
                this._q.setFromEuler(this._e.set(-1.05 + bend * .5, person.yaw, sway * .1, "YXZ"));
                this._m.compose(this._v, this._q, this._s);
            } else {
                this._m.compose(this._v.set(0, -9, 0), this._q, this._zero);
            }
            this.sleeve.setMatrixAt(flipInstance, this._m);

            // Sleeve instance i: the stack being carried, offset .22 to the
            // person's right and .06 forward, and scaled out along its
            // thickness by how many records are held.
            if (person.carry > 0) {
                const armX = -cosYaw;
                const armZ = sinYaw;
                this._v.set(person.pos.x + armX * .22 + sinYaw * .06, .94 + bob + (person.carry - 1) * .006, person.pos.z + armZ * .22 + cosYaw * .06);
                this._q.setFromEuler(this._e.set(-.22, person.yaw, .3, "YXZ"));
                this._s.set(1, 1, 1 + (person.carry - 1) * 1.4);
                this._m.compose(this._v, this._q, this._s);
                this._s.set(1, 1, 1);
            } else {
                this._m.compose(this._v.set(0, -9, 0), this._q, this._zero);
            }
            this.sleeve.setMatrixAt(i, this._m);
        }
        this.body.instanceMatrix.needsUpdate = true;
        this.sleeve.instanceMatrix.needsUpdate = true;
    }

    dispose() {
        for (const mesh of [this.body, this.sleeve]) {
            mesh.geometry.dispose();
            mesh.parent?.remove(mesh);
        }
        this.body.material.dispose();
        this.sleeve.material.dispose();
    }
}

export { Shoppers, findPath };
